import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import AdminSidebar from "../components/AdminSidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from "recharts";
import {
  DollarSign, TrendingUp, Users, CreditCard,
  ArrowUpRight, ShoppingBag, Zap, Target,
  Clock, Globe, RefreshCw, Settings, Save, X, Trash2
} from "lucide-react";

const COLORS = ["#dc2626", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1a1a1a] border border-[#333] px-3 py-2 text-sm">
        <p className="text-white font-medium">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color || "#dc2626" }}>
            {entry.name}: ${typeof entry.value === "number" ? entry.value.toLocaleString() : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const StatCard = ({ icon: Icon, label, value, prefix = "", suffix = "", trend, delay = 0 }) => (
  <motion.div
    className="bg-[#111] border border-[#222] p-5 hover:border-[#333] transition-colors"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 bg-crimson/15 flex items-center justify-center">
        <Icon className="w-4 h-4 text-crimson" />
      </div>
      {trend !== undefined && (
        <div className="flex items-center gap-1 text-xs text-emerald-400">
          <ArrowUpRight className="w-3 h-3" />
          {trend}
        </div>
      )}
    </div>
    <div className="font-heading text-2xl font-bold text-white">
      {prefix}{typeof value === "number" ? value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : value}{suffix}
    </div>
    <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">{label}</div>
  </motion.div>
);

const AdminRevenue = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOverrides, setShowOverrides] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [overrideForm, setOverrideForm] = useState({ currency: "", rate: "" });
  const [savingOverrides, setSavingOverrides] = useState(false);

  useEffect(() => {
    fetchRevenue();
    fetchOverrides();
  }, []);

  const fetchRevenue = async () => {
    try {
      const res = await fetch(`${API}/admin/revenue/dashboard`, { credentials: "include" });
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error("Failed to fetch revenue data");
      }
    } catch {
      toast.error("Failed to load revenue dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchOverrides = async () => {
    try {
      const res = await fetch(`${API}/admin/revenue/rate-overrides`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setOverrides(d.overrides || {});
      }
    } catch {}
  };

  const saveOverrides = async () => {
    setSavingOverrides(true);
    try {
      const res = await fetch(`${API}/admin/revenue/rate-overrides`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ overrides }),
      });
      if (res.ok) {
        toast.success("Rate overrides saved — rates will refresh on next load");
        fetchRevenue();
      } else {
        toast.error("Failed to save overrides");
      }
    } catch {
      toast.error("Failed to save overrides");
    } finally {
      setSavingOverrides(false);
    }
  };

  const addOverride = () => {
    const code = overrideForm.currency.toUpperCase().trim();
    const rate = parseFloat(overrideForm.rate);
    if (!code || code.length !== 3 || isNaN(rate) || rate <= 0) {
      toast.error("Enter a valid 3-letter currency code and positive rate");
      return;
    }
    setOverrides(prev => ({ ...prev, [code]: rate }));
    setOverrideForm({ currency: "", rate: "" });
  };

  const removeOverride = (code) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <AdminSidebar />
        <main className="lg:ml-64 p-6 lg:p-8 pt-20 lg:pt-8">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  const ov = data?.overview || {};
  const funnel = data?.funnel || {};

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-revenue-page">
      <AdminSidebar />
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <motion.div className="mb-8" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold">
            REVENUE <span className="text-crimson">DASHBOARD</span>
          </h1>
          <p className="text-gray-400 text-sm mt-1">Track monetization performance across all revenue streams</p>
        </motion.div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          <StatCard icon={DollarSign} label="Total Revenue" value={ov.total_revenue || 0} prefix="$" delay={0} />
          <StatCard icon={TrendingUp} label="This Month" value={ov.monthly_revenue || 0} prefix="$" delay={0.05} />
          <StatCard icon={CreditCard} label="MRR" value={ov.mrr || 0} prefix="$" delay={0.1} />
          <StatCard icon={Target} label="ARPU" value={ov.arpu || 0} prefix="$" delay={0.15} />
          <StatCard icon={ShoppingBag} label="Transactions" value={ov.total_transactions || 0} delay={0.2} />
          <StatCard icon={Clock} label="Pending" value={ov.pending_transactions || 0} delay={0.25} />
        </div>

        {/* Row 1: Revenue Trend + Conversion Funnel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Revenue Trend */}
          <motion.div
            className="bg-[#111] border border-[#222] p-6 lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            data-testid="revenue-trend-chart"
          >
            <h3 className="font-heading text-lg font-bold mb-1">REVENUE TREND</h3>
            <p className="text-xs text-gray-500 mb-4">Monthly revenue over the last 6 months</p>
            {(data?.revenue_trend || []).some(r => r.revenue > 0) ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data.revenue_trend} margin={{ left: 0, right: 10 }}>
                  <defs>
                    <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                  <XAxis dataKey="month" stroke="#555" tick={{ fill: "#888", fontSize: 11 }} />
                  <YAxis stroke="#555" tick={{ fill: "#888", fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#dc2626" fill="url(#revGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[240px] flex items-center justify-center text-gray-500 text-sm">
                No revenue data yet. Completed transactions will appear here.
              </div>
            )}
          </motion.div>

          {/* Conversion Funnel */}
          <motion.div
            className="bg-[#111] border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            data-testid="conversion-funnel"
          >
            <h3 className="font-heading text-lg font-bold mb-1">CONVERSION FUNNEL</h3>
            <p className="text-xs text-gray-500 mb-4">Free to paid user conversion</p>
            <div className="space-y-4">
              {[
                { label: "Total Users", value: funnel.total_users, color: "bg-gray-500", pct: 100 },
                { label: "Free Users", value: funnel.free_users, color: "bg-blue-500", pct: (funnel.free_users / Math.max(funnel.total_users, 1)) * 100 },
                { label: "PAYG Users", value: funnel.payg_users, color: "bg-amber-500", pct: (funnel.payg_users / Math.max(funnel.total_users, 1)) * 100 },
                { label: "Paid Subscribers", value: funnel.paid_subscribers, color: "bg-emerald-500", pct: (funnel.paid_subscribers / Math.max(funnel.total_users, 1)) * 100 },
              ].map((step, i) => (
                <div key={step.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-400">{step.label}</span>
                    <span className="text-xs font-bold text-white">{step.value || 0}</span>
                  </div>
                  <div className="h-3 bg-[#1a1a1a] overflow-hidden">
                    <motion.div
                      className={`h-full ${step.color}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${step.pct}%` }}
                      transition={{ delay: 0.5 + i * 0.1, duration: 0.5 }}
                    />
                  </div>
                </div>
              ))}
              <div className="mt-3 p-3 bg-[#0a0a0a] border border-[#1a1a1a] text-center">
                <span className="text-xs text-gray-400">Conversion Rate</span>
                <div className="font-heading text-2xl font-bold text-crimson">{funnel.conversion_rate || 0}%</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Row 2: Tier Distribution + Top Add-ons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Subscription Tier Distribution */}
          <motion.div
            className="bg-[#111] border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            data-testid="tier-distribution-chart"
          >
            <h3 className="font-heading text-lg font-bold mb-1">SUBSCRIPTION DISTRIBUTION</h3>
            <p className="text-xs text-gray-500 mb-4">Users by subscription tier</p>
            {(data?.tier_distribution || []).length > 0 ? (
              <div className="flex flex-col lg:flex-row items-center gap-4">
                <ResponsiveContainer width={200} height={200}>
                  <PieChart>
                    <Pie
                      data={data.tier_distribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="label"
                    >
                      {data.tier_distribution.map((entry, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1.5 w-full">
                  {data.tier_distribution.map((t, i) => (
                    <div key={t.tier} className="flex items-center justify-between px-2 py-1.5 bg-[#0a0a0a] border border-[#1a1a1a]">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-xs text-gray-300">{t.label}</span>
                      </div>
                      <span className="text-xs font-bold text-white">{t.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-500 text-sm">No user data</div>
            )}
          </motion.div>

          {/* Top Selling Add-ons */}
          <motion.div
            className="bg-[#111] border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            data-testid="top-addons-chart"
          >
            <h3 className="font-heading text-lg font-bold mb-1">TOP REVENUE PRODUCTS</h3>
            <p className="text-xs text-gray-500 mb-4">Best-selling add-ons and credit packs</p>
            {(data?.top_addons || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.top_addons} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" horizontal={false} />
                  <XAxis type="number" stroke="#555" tick={{ fill: "#888", fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" stroke="#555" tick={{ fill: "#ccc", fontSize: 10 }} width={140} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="revenue" name="Revenue" fill="#dc2626" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-gray-500 text-sm">No sales yet</div>
            )}
          </motion.div>
        </div>

        {/* Revenue Breakdown Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <motion.div className="bg-[#111] border border-[#222] p-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} data-testid="credit-sales-card">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-crimson" />
              <span className="text-xs uppercase tracking-wider text-gray-400">Credit Pack Sales</span>
            </div>
            <div className="font-heading text-2xl font-bold">${(data?.credit_sales_total || 0).toLocaleString()}</div>
            <p className="text-[10px] text-gray-600 mt-1">AI credits + Contact unlock credits</p>
          </motion.div>
          <motion.div className="bg-[#111] border border-[#222] p-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} data-testid="addon-sales-card">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingBag className="w-4 h-4 text-crimson" />
              <span className="text-xs uppercase tracking-wider text-gray-400">Add-on Sales</span>
            </div>
            <div className="font-heading text-2xl font-bold">${(data?.addon_sales_total || 0).toLocaleString()}</div>
            <p className="text-[10px] text-gray-600 mt-1">Promoted jobs, featured employer, outreach</p>
          </motion.div>
        </div>

        {/* Exchange Rates Card */}
        {data?.currency_info && (
          <motion.div
            className="bg-[#111] border border-[#222] p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.58 }}
            data-testid="exchange-rates-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                  <Globe className="w-4 h-4 text-crimson" />
                  LIVE EXCHANGE RATES
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  1 USD = X foreign currency &middot; Refreshed hourly
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowOverrides(!showOverrides)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border transition-colors ${
                    showOverrides ? "border-crimson text-crimson bg-crimson/10" : "border-[#333] text-gray-400 hover:text-white hover:border-[#555]"
                  }`}
                  data-testid="toggle-overrides-btn"
                >
                  <Settings className="w-3 h-3" />
                  {Object.keys(overrides).length > 0 ? `${Object.keys(overrides).length} Override${Object.keys(overrides).length > 1 ? "s" : ""}` : "Override Rates"}
                </button>
                <div className="text-right">
                  {data.currency_info.cached_at && data.currency_info.cached_at !== "fallback" ? (
                    <div className="flex items-center gap-1 text-[10px] text-emerald-400">
                      <RefreshCw className="w-3 h-3" />
                      {new Date(data.currency_info.cached_at).toLocaleTimeString()}
                    </div>
                  ) : (
                    <span className="text-[10px] text-amber-400">Fallback rates</span>
                  )}
                </div>
              </div>
            </div>

            {/* Rate Cards */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-2">
              {Object.entries(data.currency_info.conversion_rates || {})
                .filter(([k]) => k !== "USD")
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([currency, rate]) => {
                  const isOverridden = overrides[currency] !== undefined;
                  return (
                    <div
                      key={currency}
                      className={`p-3 border text-center transition-colors relative ${
                        isOverridden
                          ? "bg-amber-500/5 border-amber-500/30"
                          : currency === "NGN"
                            ? "bg-emerald-500/5 border-emerald-500/30"
                            : "bg-[#0a0a0a] border-[#1a1a1a]"
                      }`}
                      data-testid={`rate-${currency}`}
                    >
                      {isOverridden && (
                        <div className="absolute -top-1.5 right-1 text-[8px] px-1 bg-amber-500/20 text-amber-400 border border-amber-500/30">
                          OVERRIDE
                        </div>
                      )}
                      <div className="text-[10px] text-gray-500 font-mono">{currency}</div>
                      <div className={`text-sm font-bold mt-0.5 ${
                        isOverridden ? "text-amber-400" : currency === "NGN" ? "text-emerald-400" : "text-white"
                      }`}>
                        {rate >= 100 ? rate.toLocaleString(undefined, { maximumFractionDigits: 0 })
                          : rate >= 1 ? rate.toFixed(2)
                          : rate.toFixed(4)}
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Override Management Panel */}
            <AnimatePresence>
              {showOverrides && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 pt-4 border-t border-[#222]" data-testid="rate-overrides-panel">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold flex items-center gap-2">
                        <Settings className="w-3.5 h-3.5 text-amber-400" />
                        Rate Overrides
                      </h4>
                      <p className="text-[10px] text-gray-500">Override live rates with custom values (e.g., parallel market rates)</p>
                    </div>

                    {/* Add Override Form */}
                    <div className="flex gap-2 mb-3">
                      <input
                        value={overrideForm.currency}
                        onChange={(e) => setOverrideForm(prev => ({ ...prev, currency: e.target.value.toUpperCase().slice(0, 3) }))}
                        placeholder="NGN"
                        className="w-20 bg-[#0a0a0a] border border-[#222] px-2.5 py-2 text-sm text-white placeholder-gray-600 focus:border-crimson focus:outline-none font-mono text-center"
                        maxLength={3}
                        data-testid="override-currency-input"
                      />
                      <input
                        value={overrideForm.rate}
                        onChange={(e) => setOverrideForm(prev => ({ ...prev, rate: e.target.value }))}
                        placeholder="1 USD = ?"
                        type="number"
                        step="any"
                        className="flex-1 bg-[#0a0a0a] border border-[#222] px-2.5 py-2 text-sm text-white placeholder-gray-600 focus:border-crimson focus:outline-none"
                        data-testid="override-rate-input"
                      />
                      <Button
                        size="sm"
                        onClick={addOverride}
                        className="bg-amber-500/20 border border-amber-500/30 text-amber-400 hover:bg-amber-500/30 text-xs px-3"
                        data-testid="add-override-btn"
                      >
                        Add
                      </Button>
                    </div>

                    {/* Current Overrides List */}
                    {Object.keys(overrides).length > 0 ? (
                      <div className="space-y-1.5 mb-3">
                        {Object.entries(overrides).map(([code, rate]) => (
                          <div key={code} className="flex items-center justify-between bg-[#0a0a0a] border border-amber-500/20 px-3 py-2">
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-sm font-bold text-amber-400">{code}</span>
                              <span className="text-gray-500 text-xs">1 USD =</span>
                              <span className="text-white text-sm font-bold">{rate.toLocaleString()}</span>
                            </div>
                            <button
                              onClick={() => removeOverride(code)}
                              className="text-gray-500 hover:text-red-400 transition-colors"
                              data-testid={`remove-override-${code}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-600 mb-3">No overrides set. All currencies use live API rates.</p>
                    )}

                    {/* Save Button */}
                    <Button
                      onClick={saveOverrides}
                      disabled={savingOverrides}
                      className="bg-crimson hover:bg-crimson/90 text-white text-xs"
                      data-testid="save-overrides-btn"
                    >
                      <Save className="w-3 h-3 mr-1.5" />
                      {savingOverrides ? "Saving..." : "Save Overrides"}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Recent Transactions */}
        <motion.div
          className="bg-[#111] border border-[#222] p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          data-testid="recent-transactions"
        >
          <h3 className="font-heading text-lg font-bold mb-1">RECENT TRANSACTIONS</h3>
          <p className="text-xs text-gray-500 mb-4">Latest payment activity</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#222] text-left text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="pb-2 pr-4">Transaction</th>
                  <th className="pb-2 pr-4">Product</th>
                  <th className="pb-2 pr-4">Amount</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_transactions || []).map((txn, i) => (
                  <tr key={i} className="border-b border-[#111] hover:bg-[#0a0a0a]">
                    <td className="py-2.5 pr-4 text-xs text-gray-400 font-mono">{txn.transaction_id}</td>
                    <td className="py-2.5 pr-4 text-xs">{txn.product_name}</td>
                    <td className="py-2.5 pr-4 text-xs font-bold">${txn.amount}</td>
                    <td className="py-2.5 pr-4">
                      <span className={`text-[10px] px-2 py-0.5 ${
                        txn.status === "completed" ? "bg-emerald-500/20 text-emerald-400" :
                        txn.status === "pending" ? "bg-amber-500/20 text-amber-400" :
                        "bg-red-500/20 text-red-400"
                      }`}>
                        {txn.status}
                      </span>
                    </td>
                    <td className="py-2.5 text-xs text-gray-500">
                      {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : "-"}
                    </td>
                  </tr>
                ))}
                {(!data?.recent_transactions || data.recent_transactions.length === 0) && (
                  <tr><td colSpan={5} className="text-center py-8 text-gray-500 text-sm">No transactions yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default AdminRevenue;
